import type { NotificationDto, RealtimeEvent } from '@aawas/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, getSession, wsUrl } from './api';
import { useToast } from '../ui/toast';

interface RealtimeValue {
  connected: boolean;
  /** Latest p0 notification (gate events) so screens can react immediately. */
  lastGateEvent: NotificationDto | null;
}
const Ctx = createContext<RealtimeValue>({ connected: false, lastGateEvent: null });
export const useRealtime = () => useContext(Ctx);

/**
 * WebSocket fast path with automatic reconnect. The database is the source of truth: on every
 * (re)connect and on a fallback interval screens refetch over REST, so a missed socket message
 * delays an alert by seconds but can never lose one.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [connected, setConnected] = useState(false);
  const [lastGateEvent, setLast] = useState<NotificationDto | null>(null);
  const attempts = useRef(0);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout>;
    let stopped = false;

    const open = async () => {
      if (stopped || !getSession()) return;
      // Make sure the access token is fresh before authenticating the socket.
      try {
        await api('/me', { noCommunity: true });
      } catch {
        /* offline: the connect below will fail and back off */
      }
      const token = getSession()?.accessToken;
      if (!token || stopped) return;

      ws = new WebSocket(wsUrl());
      ws.onopen = () => ws!.send(JSON.stringify({ type: 'auth', token }));
      ws.onmessage = (m) => {
        let ev: RealtimeEvent | { type: 'ready' | 'pong' };
        try {
          ev = JSON.parse(m.data);
        } catch {
          return;
        }
        if (ev.type === 'ready') {
          attempts.current = 0;
          setConnected(true);
          void qc.invalidateQueries(); // re-sync everything after (re)connect
        } else if (ev.type === 'notification') {
          const n = ev.notification;
          void qc.invalidateQueries({ queryKey: ['notifications'] });
          if (n.type.startsWith('visit.') || n.priority === 'p0') {
            void qc.invalidateQueries({ queryKey: ['visits'] });
            setLast(n);
          }
          if (n.type.startsWith('ticket.')) void qc.invalidateQueries({ queryKey: ['tickets'] });
          if (n.type === 'notice.published') void qc.invalidateQueries({ queryKey: ['notices'] });
          toast.show({ title: n.title, body: n.body, tone: n.priority === 'p0' ? 'alert' : 'info' });
        } else if (ev.type === 'visit.updated') {
          void qc.invalidateQueries({ queryKey: ['visits'] });
        } else if (ev.type === 'ticket.updated') {
          void qc.invalidateQueries({ queryKey: ['tickets'] });
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (stopped) return;
        attempts.current++;
        timer = setTimeout(open, Math.min(15_000, 500 * 2 ** attempts.current));
      };
      ws.onerror = () => ws?.close();
    };

    void open();
    const online = () => {
      if (!connected) void open();
    };
    window.addEventListener('online', online);
    const keepalive = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('{"type":"ping"}'), 30_000);
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearInterval(keepalive);
      window.removeEventListener('online', online);
      ws?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc]);

  return <Ctx.Provider value={{ connected, lastGateEvent }}>{children}</Ctx.Provider>;
}

/** Refetch interval: relaxed while the socket is healthy, aggressive when it is not. */
export function usePollMs(healthy = 60_000, degraded = 8_000) {
  const { connected } = useRealtime();
  return connected ? healthy : degraded;
}
